import { useMemo } from 'react';
import { PersonaUsage, type ScenarioDto } from '@/api-gen/api.schemas';
import { getAvatarUrlWithSas } from 'app/utils/avatarUtils.ts';
import avatarPixar from '/avatar_pixar_female.webp';
import type { TranscriptMesssage } from '@/app/models/chat/transcript.ts';

interface UseAvatarLogicProps {
  scenario: ScenarioDto | undefined;
  transcript: TranscriptMesssage[] | undefined;
  portraitSrc?: string;
}

export const useAvatarLogic = ({ scenario, transcript, portraitSrc }: UseAvatarLogicProps) => {
  return useMemo(() => {
    // Compute avatar URL from scenarioPersonas, preferring Intro when playScenarioContextOn is true; otherwise Default
    const scenarioAvatarUrl = (() => {
      const personas = scenario?.scenarioPersonas ?? [];
      if (!personas.length) return undefined;

      // Prefer Intro persona only when intro is enabled AND text is present
      const hasIntroText = !!(scenario?.scenarioContextIntro && scenario.scenarioContextIntro.trim().length > 0);
      const wantIntro = !!scenario?.playScenarioContextOn && hasIntroText;
      const desiredUsage = wantIntro ? PersonaUsage.Intro : PersonaUsage.Default;

      let chosen = personas.find(sp => sp.personaUsages?.includes(desiredUsage));
      if (!chosen) {
        // Fallback: try Default, then any
        chosen = personas.find(sp => sp.personaUsages?.includes(PersonaUsage.Default)) ?? personas[0];
      }

      const url = chosen?.persona?.avatarUrl ?? undefined;
      return url ? getAvatarUrlWithSas(url) : undefined;
    })();

    // Compute latest AI avatar from transcript (if provided by backend)
    const lastAiAvatarSrc = (() => {
      if (!transcript || transcript.length === 0) return undefined;
      // Walk from the end to find the last AI message
      for (let i = transcript.length - 1; i >= 0; i--) {
        const msg = transcript[i];
        if (!msg.isUser && msg.avatarSrc) return msg.avatarSrc;
      }
      return undefined;
    })();

    const effectivePortraitSrc = lastAiAvatarSrc ?? scenarioAvatarUrl ?? portraitSrc ?? avatarPixar;

    return {
      scenarioAvatarUrl,
      lastAiAvatarSrc,
      effectivePortraitSrc
    };
  }, [scenario, transcript, portraitSrc]);
};
